import React, { useState } from 'react';
import { Formik, Field } from 'formik';
import { useMutation } from 'react-query';
import * as Yup from 'yup';
import { useSetRecoilState } from 'recoil';
import { userState, userTokenState } from '@app.shared/state';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser } from '@app.api';
import { AppHeader } from '@app.shared/UI/AppHeader';
import {
  Button,
  ResponseErrorMessage,
  StyledLoginForm,
  FormField,
  FormLabel,
  FormFieldsGrid,
  FormFooter,
  LoginFormWrapper,
} from '@app.shared/Style/Forms';
import { FieldValidationMessage } from '@app.shared/Forms';
import { useMount } from 'ahooks';

const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is Required'),
  password: Yup.string().required('Password is Required'),
});

const Login = () => {
  const [loginError, setLoginError] = useState(null);
  const setToken = useSetRecoilState(userTokenState);
  const setUser = useSetRecoilState(userState);
  const navigate = useNavigate();
  const mutation = useMutation(loginUser);

  useMount(() => {
    setToken(null);
    setUser(null);
  });

  return (
    <>
      <div className={`Container`}>
        <AppHeader>
          <h2>Sign In</h2>
          <p className="lead">Sign in to view your team's page.</p>
        </AppHeader>
        <LoginFormWrapper>
          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              setSubmitting(true);
              let data = await mutation.mutateAsync(values);
              if (Boolean(data.error)) {
                setLoginError(data.error);
              } else {
                const { name, email, major, school, graduation_year, _id, resume } = data.student;
                setUser({ name, email, major, school, graduation_year, _id, resume });
                setToken(data.session.hash);
                navigate('/dashboard');
              }
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
              <StyledLoginForm>
                {loginError && <ResponseErrorMessage>{loginError}</ResponseErrorMessage>}
                <FormFieldsGrid>
                  <FormField>
                    <FormLabel>
                      <span>
                        Email Address <small>*</small>
                      </span>
                      <Field type="email" name="email" placeholder="Email" />
                      <FieldValidationMessage errors={errors} touched={touched} name="email" />
                    </FormLabel>
                  </FormField>
                  <FormField>
                    <FormLabel>
                      <span>
                        Password <small>*</small>
                      </span>
                      <Field type="password" name="password" placeholder="Password" />
                      <FieldValidationMessage errors={errors} touched={touched} name="password" />
                    </FormLabel>
                  </FormField>
                </FormFieldsGrid>
                <FormFooter>
                  <div className="notes">
                    <p>
                      Forgot your password? <Link to="/login/forgot-password">Click here</Link>.
                    </p>
                  </div>
                  <div className="actions">
                    <Button type="submit" onClick={handleSubmit} disabled={isSubmitting}>
                      {isSubmitting ? 'Logging in...' : 'Login'}
                    </Button>
                  </div>
                </FormFooter>
              </StyledLoginForm>
            )}
          </Formik>
        </LoginFormWrapper>
      </div>
    </>
  );
};

export default Login;
